<script setup lang="ts">
interface Props {
  title: string
  subtitle?: string
  dataTestid?: string
  dir?: 'ltr' | 'rtl'
}

withDefaults(defineProps<Props>(), {
  subtitle: '',
  dataTestid: undefined,
  dir: 'ltr',
})
</script>

<template>
  <!--
    This component intentionally reuses the visual layout that the codebase
    currently gets from "OCard inline", but semantically it is not a card.

    OctoDS/Figma does not define an "inline OCard" pattern. In practice this
    layout behaves more like a form/settings section wrapper: descriptive
    header on the left, editable content panel on the right.

    We keep it as a dedicated layout component so section headers can evolve
    independently from OCard. In particular, needs like rendering helper or
    warning content directly under the description cannot be expressed with
    OCard's title/subtitle props alone.
  -->
  <section
    class="flex flex-col md:flex-row md:items-start md:space-x-6 space-y-3"
    :dir="dir"
    :data-testid="dataTestid"
  >
    <div class="flex flex-col space-y-s min-w-[200px] max-w-full md:max-w-[328px]">
      <div class="flex items-center gap-s" data-testid="title-row">
        <span class="flex typography-body-1-medium select-none">{{ title }}</span>
        <slot name="title-extra" />
      </div>
      <div v-if="subtitle" class="typography-body-2-long relative text-neutral-1">
        <p>{{ subtitle }}</p>
      </div>
      <slot name="description-extra" />
    </div>

    <div
      class="flex-1 p-xl h-[inherit] min-h-[inherit] min-w-0 radius-xl bg-neutral-0 shadow-s border border-neutral-5"
    >
      <div class="h-full min-h-0 flex flex-col space-y-4">
        <slot />
      </div>
    </div>
  </section>
</template>
